/**
 * AI Regulatory program model — V98-4-5. Pure: no React.
 *
 * Labels and derived states for the laws × systems grid, the decision
 * editor ("vN"), the obligation-sources card and the Operate / Conformity
 * tabs. Everything shown is derived from what the server sent; nothing
 * here can reveal a privileged field the server withheld.
 */
import type { CellState, ConformityStage, DeterminationOutcome, EuRiskTier, EuRole, ObligationSourcesView } from '@aigovops/core';
import type { AiGrid, AiGridCell, AiLaw } from './ai-program-api';

export const OUTCOME_LABEL: Record<DeterminationOutcome, string> = {
  applies: 'Applies',
  does_not_apply: 'Doesn’t apply',
  applies_with_conditions: 'Applies with conditions',
};

export const ROLE_LABEL: Record<EuRole, string> = { provider: 'Provider', deployer: 'Deployer', both: 'Provider and deployer' };

export const TIER_LABEL: Record<EuRiskTier, string> = {
  prohibited: 'Prohibited',
  high: 'High risk',
  limited: 'Limited risk · transparency',
  minimal: 'Minimal risk',
};

export const VISIBILITY_LABEL = { legal_only: 'Legal only', workspace: 'Workspace', external: 'Shareable externally' } as const;

const JURISDICTION_NAMES: Record<string, string> = {
  EU: 'EU', 'US-CA': 'California', 'US-CO': 'Colorado', 'US-NY-NYC': 'New York City', 'US-NY': 'New York State', 'US-TX': 'Texas',
  'US-IL': 'Illinois', 'US-UT': 'Utah', 'US-FED': 'US federal', UK: 'United Kingdom', CA: 'Canada',
};
export const jurisdictionName = (code: string): string => JURISDICTION_NAMES[code] ?? code;

/** Jurisdictions Legal can add in the decision editor (codes as the rules spell them). */
export const JURISDICTION_CHOICES: readonly string[] = ['EU', 'US-CA', 'US-CO', 'US-IL', 'US-NY', 'US-NY-NYC', 'US-TX', 'US-UT'];

export type CellTone = 'ok' | 'muted' | 'amber' | 'dim';

export interface CellDisplay {
  /** The outcome line ("Applies · provider") or the open state ("Needs decision"). */
  label: string;
  tone: CellTone;
  /** A short second line: tier, who / when, why it is open. */
  sub: string | null;
  open: boolean;
  /** In the open draft, still editable until the version is recorded. */
  in_draft: boolean;
}

const OPEN_LABEL: Record<Extract<CellState, 'needs_decision' | 'rule_changed' | 'interim'>, string> = {
  needs_decision: 'Needs decision',
  rule_changed: 'Rule changed · decide again',
  interim: 'Interim position · needs decision',
};

/**
 * What a cell shows — the outcome only; the reason is the server's to send (Legal / shared rows only).
 * V98-4 review T4-B1: a cell waiting on the owner reads "Waiting · {owner}" (pass the owner's name).
 */
export function cellDisplay(cell: AiGridCell, law: Pick<AiLaw, 'jurisdiction'>, ownerName?: string | null): CellDisplay {
  const d = cell.determination;
  if (cell.open) {
    if (cell.fact_open) return { label: `Waiting · ${ownerName ?? 'system owner'}`, tone: 'amber', sub: cell.in_force ? 'In force' : null, open: true, in_draft: false };
    const sub = cell.in_force && cell.state !== 'interim' ? 'In force' : null;
    return { label: OPEN_LABEL[cell.state as keyof typeof OPEN_LABEL] ?? 'Needs decision', tone: 'amber', sub, open: true, in_draft: false };
  }
  if (!d || !d.outcome) return { label: 'Needs decision', tone: 'amber', sub: null, open: true, in_draft: false };
  const role = law.jurisdiction === 'EU' && d.eu_role ? ` · ${ROLE_LABEL[d.eu_role].toLowerCase()}` : '';
  const tier = d.eu_risk_tier ? TIER_LABEL[d.eu_risk_tier] : null;
  const version = d.decision_version ? `v${d.decision_version}${cell.state === 'decided' ? ' draft' : ''}` : null;
  return {
    label: `${OUTCOME_LABEL[d.outcome]}${role}`,
    tone: d.outcome === 'does_not_apply' ? 'muted' : 'ok',
    sub: [tier, version].filter(Boolean).join(' · ') || null,
    open: false,
    in_draft: cell.state === 'decided',
  };
}

/** The record gate: the decision editor is blocked while any cell is open. */
export function recordGate(grid: Pick<AiGrid, 'open' | 'decision' | 'can_record' | 'decided_in_draft'>): { blocked: boolean; text: string } {
  if (!grid.decision || grid.decision.state !== 'draft') return { blocked: true, text: 'Start a draft first' };
  if (grid.open > 0) return { blocked: true, text: `Decide ${grid.open} open first` };
  return { blocked: false, text: `All ${grid.decided_in_draft} in the v${grid.decision.version} draft` };
}

/** The version the next recording creates (the open draft, else current + 1, else v1). */
export function nextVersion(sources: Pick<ObligationSourcesView, 'draft' | 'current'>): number {
  return sources.draft?.version ?? ((sources.current?.version ?? 0) + 1);
}

/** The locked ceiling line ("Org ceiling · frameworks only · set by Grace Liu, Nov 3, 2026"). */
export function ceilingText(c: ObligationSourcesView['ceiling'], fmt: (d: string) => string): { locked: boolean; text: string; by: string | null } {
  const by = c.set_by_name ? `${c.set_by_name}${c.set_at ? ` · ${fmt(c.set_at)}` : ''}` : null;
  return c.allows_legislation
    ? { locked: false, text: 'Org policy allows AI legislation and case-law signals', by }
    : { locked: true, text: 'Org policy: frameworks only · AI legislation and case-law signals stay off', by };
}

/**
 * Operate (T4-H1): "Undecided in force" = the cells the SERVER marks (`in_force_gap`, the same cells its
 * `counts.undecided_in_force` and the in-force gap work items count); interim positions; covered laws.
 */
export function operateSplit(grid: AiGrid): { gaps: AiGridCell[]; interim: AiGridCell[]; covered: AiGridCell[] } {
  const inForce = grid.cells.filter((c) => c.in_force);
  return {
    gaps: grid.cells.filter((c) => c.in_force_gap),
    interim: inForce.filter((c) => c.state === 'interim'),
    covered: inForce.filter((c) => !c.open && c.determination?.outcome && c.determination.outcome !== 'does_not_apply'),
  };
}

/** The Conformity station (StageRail "Audit" slot) for AI Regulatory, from the tiers. */
export function conformityStation(c: ConformityStage | null): { state: 'na' | 'active'; meta?: string; naReason?: string } {
  // T4-H10: 2–4 words, never a sentence.
  if (!c || c.status === 'not_applicable') return { state: 'na', naReason: 'None · no EU high-risk' };
  return { state: 'active', meta: c.status === 'prohibited' ? 'Prohibited practice' : `Conformity assessment · ${c.systems.length} system${c.systems.length === 1 ? '' : 's'}` };
}

/** T4-H6: what a proposal changes against the jurisdictions in the editor ("Adds Texas"); empty = acknowledge only. */
export function proposalChange(p: { legislation_on: boolean | null; jurisdictions: string[] }, current: readonly string[]): { adds: string[]; text: string | null } {
  const adds = p.legislation_on === false ? [] : p.jurisdictions.filter((j) => !current.includes(j));
  return { adds, text: adds.length ? `Adds ${adds.map(jurisdictionName).join(', ')}` : null };
}

/** T4-H2: the confirmation's summary of what vN changes from the recorded version. */
export function decisionDiff(
  from: { legislation_on: boolean | null; case_law_on: boolean | null; jurisdictions: string[] } | null,
  to: { legislation_on: boolean; case_law_on: boolean; jurisdictions: string[] },
): string[] {
  const out: string[] = [];
  const was = from?.legislation_on ? from.jurisdictions : [];
  const now = to.legislation_on ? to.jurisdictions : [];
  if (!!from?.legislation_on !== to.legislation_on) out.push(`AI legislation · ${to.legislation_on ? 'On' : 'Off'}`);
  if (!!from?.case_law_on !== to.case_law_on) out.push(`Case-law signals · ${to.case_law_on ? 'On' : 'Off'}`);
  const add = now.filter((j) => !was.includes(j)), drop = was.filter((j) => !now.includes(j));
  if (add.length) out.push(`Adds ${add.map(jurisdictionName).join(', ')}`);
  if (drop.length) out.push(`Drops ${drop.map(jurisdictionName).join(', ')}`);
  return out;
}

export const lawOf = (grid: AiGrid, key: string) => grid.laws.find((l) => l.law_key === key) ?? null;
export const systemName = (grid: AiGrid, id: string) => grid.systems.find((s) => s.system_id === id)?.name ?? id;
export const cellTitle = (grid: AiGrid, cell: Pick<AiGridCell, 'law_key' | 'system_id'>) => `${lawOf(grid, cell.law_key)?.label ?? cell.law_key} × ${systemName(grid, cell.system_id)}`;
